import React from 'react';
import { content, colors } from '../data/content';
import { contact, getWhatsAppLink, getEmailLink } from '../data/contact';
import { Instagram, Facebook, Mail, MapPin, Phone, ArrowUp } from 'lucide-react';

const Footer = ({ language }) => {
  const currentContent = content[language].footer;
  const isRTL = language === 'ar';

  const isValidUrl = (url) => {
    return url && url !== '#' && url !== '' && url.trim() !== '';
  };

  const socialLinks = [
    {
      icon: Instagram,
      url: currentContent.social.instagram,
      label: 'Instagram',
      color: '#E4405F'
    },
    {
      icon: Facebook,
      url: currentContent.social.facebook,
      label: 'Facebook',
      color: '#1877F2'
    },
    {
      icon: Mail,
      url: getEmailLink(contact.email.main),
      label: 'Email',
      color: colors.primary
    },
  ].filter(social => isValidUrl(social.url));

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer style={{
      background: `linear-gradient(135deg, ${colors.secondary} 0%, #1a2d5e 100%)`,
      padding: 'clamp(48px, 8vw, 80px) clamp(20px, 4vw, 40px) clamp(24px, 4vw, 32px)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.05,
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: '20%',
        [isRTL ? 'right' : 'left']: '10%',
        width: '300px',
        height: '300px',
        background: `radial-gradient(circle, ${colors.primary}20 0%, transparent 70%)`,
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 8s ease-in-out infinite'
      }}></div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 clamp(20px, 4vw, 40px)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'clamp(32px, 6vw, 64px)',
          marginBottom: 'clamp(40px, 6vw, 56px)'
        }}>
          {/* Brand Section */}
          <div style={{
            animation: 'fadeInUp 0.8s ease-out'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                overflow: 'hidden',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <img 
                  src="/tunagwaizi-uae Logo.png" 
                  alt="Tuna Gwaizi UAE Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '4px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3 style={{
                fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                fontWeight: 800,
                color: colors.accent,
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                margin: 0,
                background: `linear-gradient(135deg, ${colors.accent} 0%, #fff 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Tuna Gwaizi
              </h3>
            </div>
            <p style={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
              lineHeight: isRTL ? 1.9 : 1.8,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              marginBottom: '24px'
            }}>
              {currentContent.description || (isRTL 
                ? 'تونة طبيعية فاخرة من أعماق المحيط إلى مائدتك'
                : 'Premium natural tuna from the depths of the ocean to your table')}
            </p>
            
            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target={social.url.startsWith('mailto:') ? undefined : '_blank'}
                    rel={social.url.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = social.color;
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.1)';
                      e.currentTarget.style.boxShadow = `0 8px 24px ${social.color}50`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    aria-label={social.label}
                  >
                    <social.icon size={20} style={{ color: 'white' }} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Contact Section */}
          <div style={{
            animation: 'fadeInUp 0.8s ease-out 0.2s both'
          }}>
            <h4 style={{
              fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
              fontWeight: 700,
              color: 'white',
              marginBottom: 'clamp(20px, 4vw, 28px)',
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {currentContent.contactTitle || (isRTL ? 'اتصل بنا' : 'Contact Us')}
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(16px, 3vw, 20px)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                color: 'rgba(255, 255, 255, 0.85)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <MapPin size={18} style={{ color: colors.accent }} />
                </div>
                <div style={{
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)'
                }}>
                  <p style={{
                    fontWeight: 600,
                    color: 'white',
                    margin: 0,
                    marginBottom: '4px'
                  }}>
                    {currentContent.locationLabel || (isRTL ? 'الموقع' : 'Location')}
                  </p>
                  <p style={{ 
                    margin: 0,
                    lineHeight: isRTL ? 1.7 : 1.6
                  }}>
                    {currentContent.location || (isRTL ? 'الإمارات العربية المتحدة' : 'United Arab Emirates')}
                  </p>
                </div>
              </div>
              
              <a
                href={getEmailLink(contact.email.main)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  textDecoration: 'none',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = isRTL ? 'translateX(-8px)' : 'translateX(8px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Mail size={18} style={{ color: colors.accent }} />
                </div>
                <span style={{
                  direction: 'ltr',
                  display: 'inline-block',
                  unicodeBidi: 'embed'
                }}>{contact.email.main}</span>
              </a>

              <a
                href={getWhatsAppLink(contact.phone.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  textDecoration: 'none',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#25D366';
                  e.currentTarget.style.transform = 'translateX(8px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Phone size={18} style={{ color: colors.accent }} />
                </div>
                <span style={{
                  direction: 'ltr',
                  display: 'inline-block',
                  unicodeBidi: 'embed'
                }}>{contact.phone.display}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright & Scroll to Top */}
        <div style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          paddingTop: 'clamp(24px, 4vw, 32px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            margin: 0
          }}>
            {currentContent.copyright}
          </p>
          
          <button
            onClick={scrollToTop}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
            }}
            aria-label={isRTL ? 'العودة للأعلى' : 'Scroll to top'}
          >
            <ArrowUp size={20} style={{ color: 'white' }} />
          </button>
        </div>
      </div>
      
      {/* Floating WhatsApp Button */}
      <a
        href={getWhatsAppLink(contact.phone.whatsapp)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: 'fixed',
          bottom: 'clamp(24px, 4vw, 32px)',
          [isRTL ? 'left' : 'right']: 'clamp(24px, 4vw, 32px)',
          zIndex: 1000,
          width: 'clamp(56px, 8vw, 64px)',
          height: 'clamp(56px, 8vw, 64px)',
          background: '#25D366',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'pulse 2s ease-in-out infinite'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.15)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(37, 211, 102, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(37, 211, 102, 0.4)';
        }}
        aria-label="WhatsApp"
      >
        <svg width="32" height="32" fill="white" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </footer>
  );
};

export default Footer;
